/**
 * Compares two {@link Data} values. Most JavaScript built-ins that perform
 * sorting expect a comparator.
 *
 * @returns
 * - 0 if the two values are equal
 * - any positive value if a is greater than b
 * - any negative value if a is lesser than b
 *
 * @example
 * const byWordLength: Comparator<string> = (a, b) => a.length - b.length;
 * const words = ['a', 'sharp', 'turn'];
 * const wordsSortedByLength.toSorted(byWordLength);
 * // Perform mutative sorts with caution:
 * words.sort(byWordLength);
 */
export interface Comparator<Data> {
  (a: Data, b: Data): number;
}

/**
 * Calculates a numeric score for a {@link Data} value. Two values with the
 * same score are considered equal. Otherwise the one with the greater score is
 * the greater value.
 */
export interface Scoring<Data> {
  (value: Data): number;
}

/**
 * Creates a comparator from a scoring function. Each call to the resulting
 * comparator will call the provided scoring function twice; to avoid
 * performance issues, keep scoring functions cheap in calculation or cache its
 * scores in a weak map.
 *
 * @example
 * const byWordLength = comparatorFromScoring<string>((word) => word.length);
 * const words = ['vigor', 'vim', 'flotsam', 'jetsam'];
 * const wordsSortedByLength = words.toSorted(byWordLength);
 */
export function comparatorFromScoring<Data>(scoreOf: Scoring<Data>): Comparator<Data> {
  return Object.defineProperty((a, b) => scoreOf(a) - scoreOf(b), 'name', {
    value: `comparator(${scoreOf.name})`,
  });
}

/**
 * Creates a comparator from a static array with the desired ranking. If the
 * resulting comparator is given a value that is absent from the static array,
 * the value is treated like it has a score of `-1`.
 *
 * @example
 * type Grade = 'A' | 'B' | 'C' | 'D' | 'S';
 * const GradeRank = ['S', 'A', 'B', 'C', 'D'] satisfies Grade[];
 * const byGrade = comparatorFromRanking(GradeRank);
 * // now `byGrade` will sort `'S'` first and `'D'` last
 */
export function comparatorFromRanking<Data>(ranking: readonly Data[]): Comparator<Data> {
  return comparatorFromScoring(ranking.indexOf.bind(ranking));
}

/**
 * Creates a comparator from a key mapping function and a static ranking array.
 * This allows sorting objects based on a property that needs to be ranked according
 * to a predefined order.
 *
 * @example
 * // Sort users by their grade according to a predefined ranking
 * type Grade = 'S' | 'A' | 'B' | 'C' | 'D';
 * interface User { name: string; grade: Grade; }
 *
 * const GradeRank = ['S', 'A', 'B', 'C', 'D'] satisfies Grade[];
 * const byUserGrade = comparatorFromKeyAndRanking<User, Grade>(
 *   user => user.grade,
 *   GradeRank
 * );
 *
 * const users: User[] = [
 *   { name: 'Alice', grade: 'B' },
 *   { name: 'Bob', grade: 'S' },
 *   { name: 'Charlie', grade: 'A' }
 * ];
 *
 * // Sort users with 'S' grade first, then 'A', etc.
 * const sortedUsers = users.toSorted(byUserGrade);
 * // Result: [{ name: 'Bob', grade: 'S' }, { name: 'Charlie', grade: 'A' }, { name: 'Alice', grade: 'B' }]
 */
export function comparatorFromKeyAndRanking<Data, Key>(
  keyMapper: (value: Data) => Key,
  ranking: readonly Key[],
): Comparator<Data> {
  return comparatorFromScoring((value: Data) => ranking.indexOf(keyMapper(value)));
}

/**
 * Creates convenience methods for a given comparator, with the first operand
 * pre-bound to the given operand. This class is meant to be subclassed;
 * subclasses are expected to supply their own comparator, and are free to
 * define additional named methods that make sense when comparing the given data
 * type.
 *
 * @example
 * const byWordLength = (a: string, b: string) => a.length - b.length;
 * const WordLengthCompare = (word: string) =>
 *   new (class extends BoundComparatorOps<string> {
 *     isLongerThan = this.gt;
 *     isShorterThan = this.lt;
 *     isSameLengthAs = this.eq;
 *   })(byWordLength, word));
 */
export abstract class BoundComparatorOps<Data> {
  readonly compare: Comparator<Data>;
  #a: Data;

  constructor(comparator: Comparator<Data>, subject: Data) {
    this.compare = comparator;
    this.#a = subject;
  }

  equals = this.eq;
  isEqual = this.eq;
  isNotEqual = this.neq;

  cmp(b: Data): number {
    return this.compare(this.#a, b);
  }

  eq(b: Data): boolean {
    return this.cmp(b) === 0;
  }

  neq(b: Data): boolean {
    return !this.eq(b);
  }

  gt(b: Data): boolean {
    return this.cmp(b) > 0;
  }

  gte(b: Data): boolean {
    return this.cmp(b) >= 0;
  }

  lt(b: Data): boolean {
    return this.cmp(b) < 0;
  }

  lte(b: Data): boolean {
    return this.cmp(b) <= 0;
  }
}

/**
 * Joins multiple comparators into one. Applies each comparator in order until one returns a non-zero result.
 *
 * @example
 * const byName = (a: Person, b: Person) => a.name.localeCompare(b.name);
 * const byAge = (a: Person, b: Person) => a.age - b.age;
 * const comparePeople = joinComparators(byName, byAge);
 * const sorted = people.toSorted(comparePeople);
 */
export function joinComparators<Data>(...comparators: Comparator<Data>[]): Comparator<Data> {
  return Object.defineProperty(
    (a, b) => {
      for (const compare of comparators) {
        const result = compare(a, b);

        if (result !== 0) {
          return result;
        }
      }

      return 0;
    },
    'name',
    { value: 'joinedComparator' },
  );
}
